// src/utils/nextTreatmentBooking.js
//
// Treatment-aware "next booking" selection for the lead-detail hero.
// Operates on ONE treatment's appointments (never the first appointment on
// the lead or the most advanced treatment) and excludes anything that is not
// an active future booking: CRM CANCELLED / ATTENDED / FAILED_TO_ATTEND, and
// Dentally bookings whose provider cancellation is CANCELLED.
import moment from 'moment';

const APPOINTMENT_DATETIME_FORMAT = 'DD-MM-YYYY HH:mm:ss';
const INACTIVE_CRM_STATUSES = new Set(['CANCELLED', 'ATTENDED', 'FAILED_TO_ATTEND']);

export const parseAppointmentDatetime = (value) => {
  if (!value) return null;
  const parsed = moment(value, APPOINTMENT_DATETIME_FORMAT, true);
  return parsed.isValid() ? parsed : null;
};

export const isActiveBooking = (appointment) => {
  if (!appointment || typeof appointment !== 'object') return false;
  if (INACTIVE_CRM_STATUSES.has(appointment.status)) return false;
  if (appointment.dentally?.cancellation_state === 'CANCELLED') return false;
  return true;
};

/** Active bookings of `treatment` at or after `now`, soonest first. */
export const getUpcomingTreatmentBookings = (treatment, now = new Date()) => {
  const appointments = Array.isArray(treatment?.appointments) ? treatment.appointments : [];
  const nowMoment = moment(now);
  return appointments
    .filter(isActiveBooking)
    .map((appointment) => ({ appointment, at: parseAppointmentDatetime(appointment.datetime) }))
    .filter(({ at }) => at && !at.isBefore(nowMoment))
    .sort((a, b) => a.at.valueOf() - b.at.valueOf())
    .map(({ appointment }) => appointment);
};

/** { next, others } for the hero: the soonest active booking and how many more follow. */
export const getNextTreatmentBooking = (treatment, now = new Date()) => {
  const upcoming = getUpcomingTreatmentBookings(treatment, now);
  return { next: upcoming[0] || null, others: Math.max(upcoming.length - 1, 0) };
};

export const formatBookingWhen = (appointment) => {
  const at = parseAppointmentDatetime(appointment?.datetime);
  return at ? at.format('ddd D MMM, HH:mm') : null;
};
